'use client';

import dynamic from 'next/dynamic';

import {
	INTEGRATIONS_CELL,
	PRODUCT_STAGES,
	stageHref,
} from '../content/product/product-stages';
import {
	NavPanelCard,
	NavPanelCellLink,
	NavPanelFooterRow,
	type NavPanelCell,
} from './marketing-nav-panel-card';

/**
 * The footer note's live release title, held behind a client-only dynamic
 * boundary so the ~120 KB release-notes dataset it reads is fetched when the
 * Product menu first opens instead of shipping in every marketing route's
 * initial script set. The panel itself only ever mounts on open, so the
 * chunk resolves within the same interaction.
 */
const ProductNavLatestRelease = dynamic(
	() => import('./product-nav-latest-release').then((m) => m.ProductNavLatestRelease),
	{ ssr: false, loading: () => null },
);

/**
 * ProductNavPanel - the Product popover body (LANDING_INSPIRATION G3, the
 * Linear card-in-card): an inner hairline card holding a 3x2 grid - the five
 * stages + Integrations - then the footer row OUTSIDE the card: "New: <latest
 * shipped>" left (fed live from the same release-notes source /docs/changelog
 * renders) and the Changelog link right.
 *
 * The grid is a REAL grid-cols-3 over the flat cells in row-major order, so
 * every row shares one height and a tall benefit line can never push a single
 * column out of alignment; the column and row hairlines ride on the cells
 * themselves.
 */
export const ProductNavPanel = () => {
	const cells: NavPanelCell[] = [
		...PRODUCT_STAGES.map((stage) => ({
			label: stage.label,
			prefix: `${stage.index}.0`,
			benefit: stage.benefit,
			href: stageHref(stage),
		})),
		{
			label: INTEGRATIONS_CELL.label,
			benefit: INTEGRATIONS_CELL.benefit,
			href: INTEGRATIONS_CELL.href,
		},
	];

	return (
		<div className="p-2">
			<NavPanelCard>
				<div className="grid grid-cols-3">
					{cells.map((cell) => (
						<NavPanelCellLink
							key={cell.href}
							cell={cell}
							className="border-white/[0.06] [&:not(:nth-child(3n+1))]:border-l [&:nth-child(n+4)]:border-t"
						/>
					))}
				</div>
			</NavPanelCard>

			<NavPanelFooterRow
				note={<ProductNavLatestRelease />}
				linkLabel="Changelog"
				linkHref="/docs/changelog"
			/>
		</div>
	);
};
